import { LitElement, html } from 'lit';
import { TagKey } from '@cof/tag-catalog-fs-personal-mfe';
import { DebugService } from '../../../../shared/services/debug.service.js';
import { ApplicationService } from '../../../../services/application.service.js';
import { style } from './address-ndi-v2.style.js';
import { globalStyle } from '../../../../global-styles.style.js';
import '@cof/fs-select-wc/fs-select.js';
import '../../../edit-card/edit-card.component.js';
import { stratum } from '../../../../../constants/stratum.js';

export class RefiPersonalNDIComponent extends LitElement {
  constructor() {
    super();
    this.debug = new DebugService('RefiPersonalNDIComponent');
    this.values = {};
    this.aptPattern = '^([a-zA-Z0-9]| |-|\\.)*$';
    this.validity = {};
    this.isPrimary = false;
  }

  static styles = [style, globalStyle];

  static properties = {
    isPrimary: { type: Boolean, attribute: 'is-primary', reflect: true },
  };

  connectedCallback() {
    super.connectedCallback();
    // Element IDs
    this.coAppPrefix = this.isPrimary ? '' : 'co-';
    this.ownerStatusId = `${this.coAppPrefix}ownerStatus`;
    this.monthlyPaymentId = `${this.coAppPrefix}monthlyPayment`;
    // Set initial state
    this.application = ApplicationService.Application;
    this.resetValues();
  }

  firstUpdated() {
    super.firstUpdated();
    this.childIds = [this.ownerStatusId, this.monthlyPaymentId];
    this.addCustomPadding();
  }

  addCustomPadding() {
    setTimeout(() => {
      const toggleOwn = this.shadowRoot.getElementById(
        `${this.ownerStatusId}Own`
      );
      const toggleRent = this.shadowRoot.getElementById(
        `${this.ownerStatusId}Rent`
      );
      const toggleOther = this.shadowRoot.getElementById(
        `${this.ownerStatusId}Other`
      );

      [toggleOwn, toggleRent, toggleOther].forEach((element) => {
        if (element) {
          element.renderRoot.children[1].setAttribute('style', 'padding:13px');
        }
      });
    }, 0);
  }

  resetValues() {
    this.application = ApplicationService.Application;
    this.values = JSON.parse(
      JSON.stringify(
        this.isPrimary
          ? this.getNdiValues(this.application.primaryApplicant.currentAddress)
          : this.getNdiValues(this.application.coApplicant.currentAddress)
      )
    );
    if (!this.values.ownerStatus) {
      this.values.ownerStatus = '';
    }
    this.initialOwnerStatus = this.values.ownerStatus.toLowerCase();

    // Ensure DOM is settled before grabbing the element
    setTimeout(() => {
      const ownerStatus = this.shadowRoot.getElementById(this.ownerStatusId);
      const monthlyPayment = this.shadowRoot.getElementById(
        this.monthlyPaymentId
      );

      if (ownerStatus) {
        ownerStatus.setAttribute('value', this.values.ownerStatus);
        this.validateField({
          id: this.ownerStatusId,
          value: this.values.ownerStatus,
        });
      }
      if (monthlyPayment) {
        monthlyPayment.setAttribute('value', this.values.monthlyPayment ?? '');
        this.validateField({
          id: this.monthlyPaymentId,
          value: this.values.monthlyPayment,
        });
      }
    }, 0);
    this.requestUpdate();
  }

  getNdiValues(currentAddress) {
    return {
      ownerStatus: currentAddress.ownerStatus,
      monthlyPayment: currentAddress.monthlyPayment,
    };
  }

  render() {
    return html`${this.renderFields()}`;
  }

  renderFields() {
    return html`
      <div class="personal__owner-status">
        <fs-choice-button-group
          direction="row"
          field-label="Do ${this.isPrimary ? 'you ' : `they `} own or rent?"
        >
          <fs-choice-button
            id="${this.ownerStatusId}Own"
            name="${this.ownerStatusId}"
            alignment="center"
            label-text="Own"
            type="radio"
            value="OWN"
            value-missing-message="Please select an option"
            required
            ?selected="${this.values.ownerStatus === 'OWN'}"
            data-e2e-ref="ownerStatusOwn"
            @change="${(ev) => {
              this.setOwnerStatus(ev.target?.value);
            }}"
            @blur="${(ev) => {
              this.publishTagOnBlur(
                `ownerStatus`,
                ev,
                'Please select an option'
              );
            }}"
          ></fs-choice-button>
          <fs-choice-button
            id="${this.ownerStatusId}Rent"
            name="${this.ownerStatusId}"
            alignment="center"
            label-text="Rent"
            type="radio"
            value="RENT"
            ?selected="${this.values.ownerStatus === 'RENT'}"
            value-missing-message="Please select an option"
            required
            data-e2e-ref="ownerStatusRent"
            @change="${(ev) => {
              this.setOwnerStatus(ev.target?.value);
            }}"
            @blur="${(ev) => {
              this.publishTagOnBlur(
                `ownerStatus`,
                ev,
                'Please select an option'
              );
            }}"
          ></fs-choice-button>
          <fs-choice-button
            id="${this.ownerStatusId}Other"
            name="${this.ownerStatusId}"
            alignment="center"
            label-text="Other"
            type="radio"
            value="OTHER"
            ?selected="${this.values.ownerStatus === 'OTHER'}"
            value-missing-message="Please select an option"
            required
            data-e2e-ref="ownerStatusOther"
            @change="${(ev) => {
              this.setOwnerStatus(ev.target?.value);
            }}"
            @blur="${(ev) => {
              this.publishTagOnBlur(
                `ownerStatus`,
                ev,
                'Please select an option'
              );
            }}"
          ></fs-choice-button>
        </fs-choice-button-group>
        <fs-body-text
          size="small"
          variant="feedback-negative"
          ?hidden="${this.hideError(this.ownerStatusId)}"
          id="ownerStatusToggleInvalid"
        >
          Please select an option
        </fs-body-text>
        <div class="personal__housing-monthly-payment">
          <fs-currency-field
            id="${this.monthlyPaymentId}"
            name="${this.monthlyPaymentId}"
            min="0"
            max="199999"
            decimal-separator="none"
            maximum-fraction-digits="0"
            minimum-decimal-percision="0"
            autocomplete="on"
            required
            value="${this.values.monthlyPayment}"
            data-e2e-ref="housingMonthlyPayment"
            @input="${(ev) => {
              this.setMonthlyPaymentValue(
                ev,
                ev.target?.value.replace(/[$,]/g, '')
              );
            }}"
            @blur="${(ev) => {
              this.publishTagOnBlur(
                'housingMonthlyExpense',
                ev,
                'Please enter a valid amount'
              );
            }}"
            value-missing-message="Please enter a valid amount"
            range-underflow-message="Amount entered should be at least $0"
          >
            What’s ${this.isPrimary ? 'your' : 'their'} monthly housing expense?
          </fs-currency-field>
        </div>
      </div>
    `;
  }

  hideError(id) {
    return this.validity[id] === undefined || this.validity[id] === true;
  }

  setMonthlyPaymentValue(ev, _value) {
    this.setValue({ id: this.monthlyPaymentId, value: _value });
    this.validateField({ id: this.monthlyPaymentId, value: _value });
  }

  setOwnerStatus(_value) {
    this.setValue({ id: this.ownerStatusId, value: _value });
    this.validateField({ id: this.ownerStatusId, value: _value });
  }

  runBusinessChecks() {
    this.childIds.forEach((_id) => {
      const element = {
        id: _id,
        value: this.shadowRoot.getElementById(_id)?.value,
      };
      this.validateField(element);
    });
  }

  validateField(element) {
    let valid = false;
    // Validate business rules
    if (element.id === this.ownerStatusId) {
      valid = ['OWN', 'RENT', 'OTHER'].includes(this.values.ownerStatus);
    } else if (element.id === this.monthlyPaymentId) {
      const val = Math.abs(this.values.monthlyPayment);
      valid = !Number.isNaN(parseInt(val, 10));
    }

    this.updateValidity({
      detail: {
        name: element.id,
        isValid: valid,
      },
    });
  }

  updateValidity(ev) {
    this.validity[ev.detail.name] = ev.detail.isValid;
    this.dispatchEvent(
      new CustomEvent('validity', {
        detail: {
          name: ev.detail.name,
          isValid: ev.detail.isValid,
        },
      })
    );

    this.requestUpdate();
  }

  setValue(element) {
    const filteredName = element.id.replace(this.coAppPrefix, '');
    if (element.id === this.monthlyPaymentId) {
      // Converting negative pasted values to positive values, as fs-currency-field bug doesn't currently invalidate it
      this.values[filteredName] = Math.abs(element.value);
    } else {
      this.values[filteredName] = element.value;
    }
  }

  saveToApplication() {
    if (this.initialOwnerStatus !== this.values.ownerStatus.toLowerCase()) {
      stratum.publishTag(TagKey.PQ_TEXT_INPUT_FIELD_CHANGED_FIELD, {
        replacements: {
          value: this.isPrimary
            ? `ownerStatus: ${this.values.ownerStatus.toLowerCase()}`
            : `co-ownerStatus: ${this.values.ownerStatus.toLowerCase()}`,
        },
      });
    }
    this.childIds.forEach((id) => {
      const filteredName = id.replace(this.coAppPrefix, '');
      if (id === this.monthlyPaymentId) {
        this.application[
          this.isPrimary ? 'primaryApplicant' : 'coApplicant'
        ].currentAddress[filteredName] = Math.abs(this.values.monthlyPayment);
      } else if (this.values[filteredName] !== '') {
        this.application[
          this.isPrimary ? 'primaryApplicant' : 'coApplicant'
        ].currentAddress[filteredName] = this.values[filteredName];
      }
    });

    this.requestUpdate();
  }

  publishTagOnBlur(id, ev, errorMessage) {
    if (ev?.target?.validity?.valid === undefined) {
      return;
    }
    // Publish tag on blur
    if (ev.target.validity.valid) {
      stratum.publishTag(TagKey.PQ_TEXT_INPUT_FIELD_EDITED_FIELD, {
        replacements: { value: this.isPrimary ? id : `co-${id}` },
      });
    } else {
      // when format validation fails
      stratum.publishTag(
        TagKey.PQ_TEXT_INPUT_FIELD_FIELD_VALIDATION_ERROR_MESSAGE,
        {
          replacements: {
            value: errorMessage,
          },
        }
      );
    }
  }
}

customElements.define('refi-address-ndi-v2', RefiPersonalNDIComponent);
